import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  AfterViewInit,
  inject,
} from '@angular/core';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { UserSettings, I18nService, UiService } from '@lp-ui-lib';
import { ValueRange, ValueRangeType } from '../../util/visualizations/value-range';
import {
  ConstructVizualization,
  VisualizationType,
} from '../../util/visualizations/visualization-type';
import { Visualization } from '../../util/visualizations/visualization.interface';
import { GraphData } from '../../models/classes/telemetry_history';

@Component({
  selector: 'app-graph',
  templateUrl: './graph.html',
  styleUrls: ['./graph.scss'],
  imports: [NzIconModule, NgScrollbarModule],
})
export class GraphComponent implements AfterViewInit {
  private data: GraphData | any = null;
  private chart: Visualization | any = null;
  public type: VisualizationType | any = null;
  public guid: string = uuidv4();

  // range:
  public historyStartDate: Date = new Date('01 Jan 1980 00:00:00 GMT');
  public historyEndDate: Date = new Date();
  public rangeStartDate: Date = new Date();
  public rangeEndDate: Date = new Date();
  public customRange = false;
  public isBrush = false; // brushing (zooming by selecting an area) is active

  @Input() hasResetButton = true;
  @Input() set Type(type: string) {
    switch (type.toLocaleLowerCase()) {
      case 'multi-line':
        this.type = VisualizationType.multi_line;
        break;
      case 'gantt':
        this.type = VisualizationType.gantt;
        break;
      case 'bars':
        this.type = VisualizationType.bars;
        break;
      case 'pie':
        this.type = VisualizationType.pie;
        break;
      default:
        this.type = VisualizationType.line;
    }
    if (
      !!this.data &&
      !!this.settings &&
      !!this.rangeStartDate &&
      !!this.rangeEndDate &&
      !!this.type
    ) {
      this.drawGraph();
    }
  }

  get Scrollable(): boolean | any {
    return this.type === VisualizationType.bars || this.type === VisualizationType.gantt; // currently only bar charts are scrollable
  }

  @Input() displayResetBtn = false;

  @Input() set Data(data: GraphData) {
    this.data = data;
    if (
      !!this.data &&
      !!this.settings &&
      !!this.rangeStartDate &&
      !!this.rangeEndDate &&
      !!this.type &&
      !!this.chartContainer
    ) {
      this.drawGraph();
      this.chart?.SetXDomain(this.rangeStartDate, this.rangeEndDate, this.customRange);
    }
  }

  @Input() set Timeframe(timeframe: any) {
    if (!timeframe) return;
    if (!this.rangeStartDate || !this.rangeEndDate) {
      this.rangeStartDate = timeframe.start;
      this.rangeEndDate = timeframe.end;
      this.customRange = timeframe.custom;
      if (
        !!this.data &&
        !!this.settings &&
        !!this.rangeStartDate &&
        !!this.rangeEndDate &&
        !!this.type &&
        !!this.chartContainer
      ) {
        this.drawGraph();
      }
    } else {
      this.rangeStartDate = timeframe.start;
      this.rangeEndDate = timeframe.end;
      this.customRange = timeframe.custom;
      if (
        !!this.data &&
        !!this.settings &&
        !!this.rangeStartDate &&
        !!this.rangeEndDate &&
        !!this.type
      ) {
        this.chart?.SetXDomain(this.rangeStartDate, this.rangeEndDate, this.customRange);
      }
    }
  }

  private settings: UserSettings | null = null;
  @Input() set Settings(settings: UserSettings) {
    if (!this.settings) {
      this.settings = settings;
      if (
        !!this.data &&
        !!this.settings &&
        !!this.rangeStartDate &&
        !!this.rangeEndDate &&
        !!this.type &&
        !!this.chartContainer
      ) {
        this.drawGraph(true);
        this.chart?.SetXDomain(this.rangeStartDate, this.rangeEndDate, this.customRange);
      }
    } else {
      this.settings = settings;
      if (
        !!this.data &&
        !!this.chart &&
        !!this.settings &&
        !!this.rangeStartDate &&
        !!this.rangeEndDate &&
        !!this.type
      ) {
        this.chart.SetXDomain(this.rangeStartDate, this.rangeEndDate, this.customRange);
      }
    }
  }

  @Output() brushStateChange = new EventEmitter<boolean>();
  @Output() timeframeUpdated: EventEmitter<string[]> = new EventEmitter<string[]>();

  @Output() timeframeReset = new EventEmitter<void>();
  @ViewChild('chart', { static: false })
  private chartContainer: ElementRef | null = null;

  @ViewChild('chartFixed', { static: false })
  private chartContainerFixed: ElementRef | null = null;

  @ViewChild('scrollable', { static: false })
  private scrollable: ElementRef | null = null;

  get IsPie(): boolean {
    return this.type === VisualizationType.pie;
  }
  public ui = inject(UiService);
  private i18n = inject(I18nService);
  public changeRef = inject(ChangeDetectorRef);

  ngAfterViewInit() {
    if (
      !!this.data &&
      !!this.settings &&
      !!this.rangeStartDate &&
      !!this.rangeEndDate &&
      !!this.type &&
      !!this.chartContainer
    ) {
      this.drawGraph(true);
      this.chart?.SetXDomain(this.rangeStartDate, this.rangeEndDate, this.customRange);
    }
  }

  drawGraph(keepExtent = false) {
    if (!this.data) return;
    this.chart = ConstructVizualization(
      this.type,
      this.Scrollable
        ? [this.chartContainer, this.chartContainerFixed, this.scrollable]
        : [this.chartContainer],
      'lc' + this.data.metadata.measurementTypeID + this.guid,
      this.i18n
    );
    if (!this.chart) return;
    if (!keepExtent) this.chart.ResetXDomain();

    if (
      this.type === VisualizationType.multi_line &&
      (this.data.metadata.measurementTypeID === 1073 ||
        this.data.metadata.measurementTypeID === 1074)
    ) {
      this.chart['criticalOverride'] = this.data.metadata.critical;
      this.chart['warningOverride'] = this.data.metadata.warning;
    }
    let minY = Infinity;
    let maxY = -Infinity;

    let minThreshold = 0;
    let maxThreshold = 0;

    for (let i = 0; i < this.data.thresholds.length; i++) {
      if (minY > this.data.thresholds[i].thresholdMin) {
        minY = this.data.thresholds[i].thresholdMin;
        minThreshold = i;
      }

      if (maxY < this.data.thresholds[i].thresholdMax) {
        maxY = this.data.thresholds[i].thresholdMax;
        maxThreshold = i;
      }
    }

    const ranges = this.data.thresholds.map(
      (item: any, index: any) =>
        new ValueRange(
          index === minThreshold ? minY : item.thresholdMin,
          index === maxThreshold ? maxY : item.thresholdMax,
          item.error
            ? ValueRangeType.red
            : item.warning
              ? ValueRangeType.yellow
              : ValueRangeType.green,
          item.measurementTypeID
        )
    );
    this.chart.SetValueUnit(this.data.metadata.unit ? this.data.metadata.unit : '');
    if (this.data.metadata.zeroBasedValueRange && minY > 0) minY = 0;
    const booleanDomain =
      this.data.metadata.valueType === 'flag' || this.data.metadata.valueType === 'boolean';
    if (this.data.metadata.measurementTypeID === 1028) {
      const min = Math.max.apply(
        null,
        this.data.telemetry.map((item: any) => item.value)
      );
      const max = Math.min.apply(
        null,
        this.data.telemetry.map((item: any) => item.value)
      );
      if (min < -90) {
        minY = -90;
      } else if (min === 0 && min > max) {
        minY = max + 10;
      } else {
        minY = min;
      }

      if (max === 0 || max === min || max > -120) {
        maxY = -120;
      } else {
        maxY = max - 10;
      }
    }
    this.chart.SetYDomain([minY, maxY], booleanDomain, this.data.metadata.measurementTypeID);
    if (booleanDomain) {
      this.chart['booleanDomainTrueMessage'] = this.i18n.langString('tilt_true_message');
      this.chart['booleanDomainFalseMessage'] = this.i18n.langString('tilt_false_message');
    }

    this.chart.SetValueRanges(ranges);
    this.chart.render(this.data.telemetry, this.ui.IsMobile() || this.ui.IsTablet(), this.settings);
    this.chart.SetXDomain(this.rangeStartDate, this.rangeEndDate, this.customRange);
    this.isBrush = false;
    this.chart.OnBrush((x_domain: any) => {
      if (!x_domain) return;
      this.isBrush = true;
      this.brushStateChange.emit(true);
      this.timeframeUpdated.emit([x_domain[0], x_domain[1], true]);
      this.changeRef.detectChanges();
    });
    this.chart.OnBrushReset((x_domain: any) => {
      if (!x_domain) return;
      this.isBrush = false;
      this.brushStateChange.emit(false);
      this.timeframeUpdated.emit([x_domain[0], x_domain[1], false]);
      this.changeRef.detectChanges();
    });
  }

  onResize() {
    this.drawGraph(true);
  }

  onReset() {
    if (this.isBrush) this.onResetBrush();
    this.onResetTimeframe();

    this.brushStateChange.emit(false);
  }

  onResetTimeframe() {
    this.timeframeReset.emit();
  }

  onResetBrush() {
    this.chart.ResetBrush();
    this.isBrush = false;
  }
}
function uuidv4() {
  // source: https://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid
  let d = new Date().getTime(); // Timestamp
  let d2 = (typeof performance !== 'undefined' && performance.now && performance.now() * 1000) || 0; // Time in microseconds since page-load or 0 if unsupported
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    let r = Math.random() * 16; // random number between 0 and 16
    if (d > 0) {
      // Use timestamp until depleted
      r = ((d + r) % 16) | 0;
      d = Math.floor(d / 16);
    } else {
      // Use microseconds since page-load if supported
      r = ((d2 + r) % 16) | 0;
      d2 = Math.floor(d2 / 16);
    }
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}
